/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 6th 2022, 11:32:35 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { Portal, PortalValidationSchema } from "lib/schemas";
import { APISERVICEPATHS, useAPIRequest, useAPIForm } from "lib/api";
import { useGlobalScope } from "core/providers/GlobalScopeProvider";

const mainSiteAPIPath = APISERVICEPATHS.mainSiteDetails;

interface UseAPIGetMainSiteSpec {
  setValidAPIResponse: (response: Portal) => void;
}

const useAPIGetMainSite = ({ setValidAPIResponse }: UseAPIGetMainSiteSpec) => {
  const apiRequestReturn = useAPIRequest<Portal>({ apiServicePath: mainSiteAPIPath, makeRequestOnInitialMount: true });

  const { makeRequest } = apiRequestReturn;

  React.useEffect(() => {
    makeRequest({ setValidAPIResponse });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeRequest]);

  return apiRequestReturn;
};

const portalsAPIServicePath = APISERVICEPATHS.portals;

interface UsePortalAPIFormArgs {
  portalId?: Portal["id"];
  newPortalDefault?: Portal;
}

const usePortalAPIForm = ({ portalId = null, newPortalDefault = null }: UsePortalAPIFormArgs) => {
  const { globalState } = useGlobalScope();

  const [portal, setPortal] = React.useState<Portal>(
    newPortalDefault ?? {
      name: "",
      domain: "",
      organization: globalState.selectedOrganization?.id,
    }
  );

  const [successMessage, setSuccessMessage] = React.useState<string>("");

  const useAPIFormReturn = useAPIForm<Portal>({
    apiFormDefaultValues: portal,
    apiFormDataValidationSchema: PortalValidationSchema,
    setAPIFormResponse: setPortal,
    apiInitialFetchId: portalId,
    apiServicePath: portalsAPIServicePath,
    primaryKey: "id",
    successNotificationMessage: successMessage,
  });

  // Set a success message based on whether this is a PUT or POST
  React.useEffect(() => {
    setSuccessMessage(
      `Portal ${portal?.domain} for ${portal?.organization_name} was successfully ${
        useAPIFormReturn.isAPIDataUpdate ? "updated" : "created"
      }`
    );
  }, [portal?.domain, portal?.organization_name, useAPIFormReturn.isAPIDataUpdate]);

  return { portal, useAPIFormReturn };
};

const portalMenuKeysUrl = APISERVICEPATHS.portalMenuKeys;

const useFetchPortalMenuKeys = () => {
  return useAPIRequest<[string, string][]>({ apiServicePath: portalMenuKeysUrl, makeRequestOnInitialMount: false });
};

export const usePortalsAPI = () => ({
  useAPIGetMainSite,
  usePortalAPIForm,
  useFetchPortalMenuKeys,
});
